'use client'

import { AlertCircle } from 'lucide-react'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'
import {
    getTurnOffCompanyDomainActionLabel,
    type IPreparedCompanyDomainManagementAction,
} from '@/features/settings-modal/tabs/organization/utils/company-domain-management'

interface ICompanyDomainManagementConfirmationModalProps {
    preparedAction: IPreparedCompanyDomainManagementAction | null
    errorMessage: string | null
    confirmDisabled: boolean
    onOpenChange: (open: boolean) => void
    onConfirm: () => Promise<void>
}

/** Confirms reversible enrollment changes and acknowledged destructive domain removal. */
export function CompanyDomainManagementConfirmationModal({
    preparedAction,
    errorMessage,
    confirmDisabled,
    onOpenChange,
    onConfirm,
}: ICompanyDomainManagementConfirmationModalProps) {
    const isRemoval = preparedAction?.action === 'remove_domain'
    const domainName = preparedAction?.domainName ?? ''
    const turnOffLabel = getTurnOffCompanyDomainActionLabel(preparedAction?.enrollmentMode ?? 'automatic_suggestion')

    return (
        <ConfirmationModal
            open={preparedAction !== null}
            onOpenChange={onOpenChange}
            title={isRemoval ? `Remove ${domainName}?` : `${turnOffLabel} for ${domainName}?`}
            subtitle={
                isRemoval
                    ? 'This releases the domain from this workspace.'
                    : 'The domain stays claimed by this workspace.'
            }
            content={
                isRemoval
                    ? `New coworkers will no longer discover this workspace through ${domainName}, and another workspace may be able to claim it. Existing members keep access.`
                    : preparedAction?.enrollmentMode === 'automatic_invitation'
                      ? `New coworkers with ${domainName} email addresses will no longer be invited automatically. Existing members keep access.`
                      : `New coworkers with ${domainName} email addresses will no longer discover this workspace as a suggestion. Existing members keep access.`
            }
            confirmText={isRemoval ? 'Remove domain' : turnOffLabel}
            confirmDisabled={confirmDisabled}
            requireConfirmation={isRemoval}
            destructive={isRemoval}
            onConfirm={onConfirm}
        >
            {errorMessage && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <p className="text-sm text-destructive" role="alert">
                        {errorMessage}
                    </p>
                </div>
            )}
        </ConfirmationModal>
    )
}
